import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {Alert, App, Button, Flex, Form, Input, Modal, Spin, Switch, Table, Tree, TreeDataNode, Typography} from "antd"
import { useAuth } from "../hooks/useAuth";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import {
    AndroidApplication,
    APIKey,
    ApplicationDetail,
    iOSApplication,
    SearchConfiguration,
    WebApplication
} from "../interfaces/OrgDetail.interface";
import { toTitleCase } from "../utils/string.utils";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { Key, ReactNode, useEffect, useState } from "react";
import { Rule } from "antd/es/form";
import { json } from "stream/consumers";
import { FileUploader } from "./InputHelpers/FileUploader";
import { base64ToString } from "../utils/file.utils";
import { ObjectToEditForm } from "./InputHelpers/ObjectToEditForm";
import { ObjectToTable } from "./ObjectToTable";

export const OrgDetail = (props:{ organisationId: string})=>{
    const [selectedApp, setSelectedApp] = useState<ApplicationDetail|null>(null);
    const auth = useAuth();
    const queryClient = useQueryClient();
    const { message } = App.useApp()

    const searchConfigMutator = useMutation({
        mutationFn: (options:{searchConfig: SearchConfiguration, appId: string, orgId: string}) => (
            auth.api.saveSearchConfiguration(options.searchConfig, options.appId, options.orgId)
        ),
        onSuccess: (r) => {
            if (!r) {
                message.error("Failed to save search configuration. If search is enabled all URL Fields are required");
                return;
            }

            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: (err) => {
            message.error("Failed to save search configuration");
            console.error("Failed to save search config", err)
        }
    })

    const webConfigMutator = useMutation({
        mutationFn: (options:{webApp: WebApplication, appId:string, orgId:string})=>auth.api.saveWebApplication(options.webApp, options.appId, options.orgId),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: () => {
            console.error("Oops we failed to save web app");
            message.error("Oops we failed to save web app");
        }
    })
    const androidAppMutator = useMutation({
        mutationFn: (options:{androidApp: AndroidApplication, appId:string, orgId:string})=>auth.api.saveAndroidApplication(options.androidApp, options.appId, options.orgId),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: () => {
            console.error("Oops we failed to save Android app");
            message.error("Oops we failed to save Android app");
        }
    })

    const iosAppMutator = useMutation({
        mutationFn: (options:{iosApp: iOSApplication, appId:string, orgId:string})=>auth.api.saveIOSApplication(options.iosApp, options.appId, options.orgId),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: () => {
            console.error("Oops we failed to save iOS app");
            message.error("Oops we failed to save iOS app");
        }
    })

    const apiKeyMutator = useMutation({
        mutationFn: (name:string)=>auth.api.createAPIKey(name, selectedApp?.id ?? "", props.organisationId),
        onSuccess: ()=>{
             // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: () => {
            console.error("Oops we failed to create a new API Key");
            message.error("Oops we failed to create a new API Key");
        }
    })

    const apiKeyEnabledMutator = useMutation({
        mutationFn: (options:{id:string, enabled: boolean})=>auth.api.updateAPIStatus(options.enabled, options.id, selectedApp?.id ?? "", props.organisationId),
        onSuccess: ()=>{
             // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['orgdetail', props.organisationId] })
        },
        onError: () => {
            console.error("Oops we failed to update API Key status");
            message.error("Oops we failed to update API Key status");
        }
    })
    let orgDetailQuery = useQuery({ queryKey: ['orgdetail', props.organisationId], queryFn: ()=> auth.api.getOrgDetail(props.organisationId)})
    if(orgDetailQuery.isLoading)
    {
        return (<Flex justify="center"><Spin indicator={<LoadingOutlined spin />} size="large" /></Flex>)
    } else if (orgDetailQuery.data && !selectedApp)
    {
        setSelectedApp(orgDetailQuery.data.apps[0]);
    } else if(orgDetailQuery.data && selectedApp)
    {
        let newApp = orgDetailQuery.data.apps.find(x=>x.id == selectedApp.id);
        if(newApp && (JSON.stringify(newApp) != JSON.stringify(selectedApp)))
        {
            setSelectedApp(newApp);
        }
        
    }
    const treeData: TreeDataNode[] = (orgDetailQuery.data?.apps ?? []).map(app=>{
        return {
            title: app.name,
            key: app.id
        }
    });

    const onSelect = (selectedKeys: Key[], info: {
            event: 'select';
            selected: boolean;
            node: TreeDataNode
            selectedNodes: TreeDataNode[];
            nativeEvent: MouseEvent;
        }) => {

            console.log("selected", selectedKeys, info.node);
            setSelectedApp(orgDetailQuery.data?.apps.find(x=>x.id == info.node.key) ?? null)
    }

    return (
        <Flex gap={10}>
             <Tree
                defaultSelectedKeys={[selectedApp?.id ?? "unknown"]}
                onSelect={onSelect}
                // onCheck={onCheck}
                showLine
                treeData={treeData}
                />

            <Flex vertical gap={10}>
                { selectedApp && <Flex key={selectedApp.id} vertical gap={10}>
                    <Typography.Title level={4}>{selectedApp.name} <Typography.Text>(<Typography.Text copyable>{selectedApp.id}</Typography.Text>)</Typography.Text></Typography.Title>

                    <Flex justify="space-between" align="baseline" gap={10}>
                        <Typography.Title level={5}>Search Configuration</Typography.Title>
                        <ObjectToEditForm
                            action={"Edit"}
                            title={"Edit Search Config"}
                            object={{
                                headendBaseUrl: selectedApp?.searchConfiguration?.headendBaseUrl ?? '',
                                searchBaseUrl: selectedApp?.searchConfiguration?.searchBaseUrl ?? '',
                                searchApiKey: selectedApp?.searchConfiguration?.searchApiKey ?? '',
                                searchIndexName: selectedApp?.searchConfiguration?.searchIndexName ?? '',
                                deeplinkBaseUrl: selectedApp?.searchConfiguration?.deeplinkBaseUrl ?? '',
                            }}
                            filter={(key) => !['applicationId','id', 'createdAt', 'updatedAt'].includes(key)}
                            onSave={async function(data: any): Promise<boolean> {
                                const result = await searchConfigMutator.mutateAsync({
                                    searchConfig: data,
                                    appId:selectedApp.id,
                                    orgId: props.organisationId
                                });

                                return !!result;
                            } }
                            rules={{
                                headendBaseUrl:[{ type: "url", required: false, min: 5, message: "HeadendBaseUrl should be a valid url"}],
                                searchBaseUrl:[{ type: "url", required: false, min: 5, message: "SearchBaseUrl should be a valid url"}],
                                deeplinkBaseUrl:[{ type: "url", required: false, min: 5, message: "DeeplinkBaseUrl should be a valid url"}],
                            }}
                        />
                    </Flex>
                    {selectedApp?.searchConfiguration && <ObjectToTable object={selectedApp?.searchConfiguration}  filter={(key)=>!['applicationId', 'id', 'createdAt','updatedAt'].includes(key)}/>}
                    {selectedApp && !selectedApp.searchConfiguration && <><Alert type='info' message='Search not configured' /></>}

                    <Flex justify="space-between" align="baseline" gap={10}>
                        <Typography.Title level={5}>Api Keys</Typography.Title>
                        <ObjectToEditForm 
                            title={"Create new API Key"}
                            action={"New"}
                            object={{
                                name: "",
                            }}
                            filter={(key) => !['applicationId','id', 'createdAt', 'updatedAt'].includes(key)} 
                            onSave={async function(data: any): Promise<boolean> {
                                try {
                                    data.firebaseConfig = JSON.parse(data.firebaseConfig)
                                } catch (err) {
                                    data.firebaseConfig = {}
                                }
                                
                                let result = await apiKeyMutator.mutateAsync(data.name)
                                console.log("apiKey save result", result, !!result);
                                return !!result;
                            } }
                            rules={{
                                name:[{ type: "string", required: true, min: 5, message: "Name should be at least 5 characters long"}],
                            }}
                        />
                    </Flex>
                    
                    <Table
                        sticky
                        style={{
                            width: '100%',
                            minWidth: '100%'
                        }}
                        columns={[
                            { title: 'Name', dataIndex: 'name', key: 'name', width: '20%'},
                            { title: 'Enabled', dataIndex: 'enabled', key: 'enabled', width: '20%', render: (_, record:APIKey)=>{
                                return  (<Switch value={record.enabled} onChange={async (checked:boolean, _)=>{
                                    await apiKeyEnabledMutator.mutateAsync({id:record.id, enabled:checked})
                                }}  />)
                            }},
                            { title: "ApiKey", dataIndex: 'id', key: 'id', render: (field, record:APIKey)=>(
                                <Typography.Text copyable={{ text: record.id, format: 'text/plain'}} style={{width: '100%', display: "inline-block"}}>
                                    <pre style={{ width: 'calc(100% - 40px)',display:"inline-flex"}}>
                                        {record.id}
                                    </pre>
                                </Typography.Text>
                            )}
                        ]}
                        rowKey={(data:APIKey)=>data.id}
                        dataSource={selectedApp.apiKeys}
                        pagination={false}
                    >
                    </Table>
     
                    <Flex justify="space-between" align="baseline" gap={10}>
                        <Typography.Title level={5}>Web Config</Typography.Title>
                        <ObjectToEditForm 
                            action={"Edit"}
                            title={"Edit Web Config"}
                            object={{
                                name: selectedApp?.webApplication?.name ?? "",
                                url: selectedApp?.webApplication?.url ?? "",
                                vapidPrivateKey: selectedApp.webApplication?.vapidPrivateKey ?? "",
                                vapidPublicKey: selectedApp.webApplication?.vapidPublicKey ?? "",
                                firebaseConfig: (selectedApp?.webApplication?.firebaseConfig ? JSON.stringify(selectedApp?.webApplication?.firebaseConfig, null, 5) : JSON.stringify({
                                    apiKey: "",
                                    authDomain: "",
                                    projectId: "",
                                    storageBucket: "",
                                    messagingSenderId: "",
                                    appId: "",
                                    measurementId: ""
                                },null, 5)),
                                applicationId: selectedApp.id
                            }}
                            filter={(key) => !['applicationId','id', 'createdAt', 'updatedAt'].includes(key)} 
                            onSave={async function(data: any): Promise<boolean> {
                                try {
                                    data.firebaseConfig = JSON.parse(data.firebaseConfig)
                                } catch (err) {
                                    data.firebaseConfig = {}
                                }
                                let payload = { webApp: data, appId:selectedApp.id, orgId: props.organisationId};
                                let result = await webConfigMutator.mutateAsync(payload)
                                console.log("webConfig save result", result, payload, !!result);
                                return !!result;
                            } }
                            rules={{
                                name:[{ type: "string", required: true, min: 5, message: "Name should be at least 5 characters long"}],
                                url:[{ type: "url", required: true, min: 5, message: "Url should be a valid url"}],
                                firebaseConfig:[{ type: "string", required: true, message: "Firebase Config should be a JSON serialisable string" , validator: (_, value:string) => {
                                    return new Promise<void>((resolve, reject)=>{
                                        let isValidJSON = false;
                                        try {
                                            JSON.parse(value);
                                            isValidJSON = true;
                                        } catch (jsonError:any) {
                                            reject(new Error("Firebase Config should be a JSON serialisable string"));
                                        } finally {
                                            resolve();    
                                        }
                                    })

                                   
                                }}],
                                vapidPrivateKey:[{ type: "string", required: true, min: 10, message: "VAPID Private Key should be a valid VAPID private key"}],
                                vapidPublicKey:[{ type: "string", required: true, min: 10, message: "VAPID Public Key should be a valid VAPID public key"}],
                            }}
                        />
                    </Flex>
                    {selectedApp.webApplication && <ObjectToTable object={sortObjectKeys(selectedApp.webApplication, ['applicationId', 'id', 'name', 'url'])}  filter={(key)=>!['applicationId', 'id', 'createdAt','updatedAt'].includes(key)}/>}

                    <Flex justify="space-between" align="baseline" gap={10}>
                        <Typography.Title level={5}>Android Config</Typography.Title>
                        <ObjectToEditForm 
                            action={"Edit"}
                            title={"Edit Android Config"}
                            object={{
                                serviceAccountJSON: selectedApp?.androidApplication?.serviceAccountJSON ? JSON.stringify(selectedApp?.androidApplication?.serviceAccountJSON, null, 5) : JSON.stringify({

                                }, null, 5),
                                firebaseProjectId: selectedApp?.androidApplication?.firebaseProjectId ?? "",
                                applicationId: selectedApp.id
                            }}
                            filter={(key) => !['applicationId','id', 'createdAt', 'updatedAt'].includes(key)} 
                            onSave={async function(data: any): Promise<boolean> {
                                try {
                                    data.serviceAccountJSON = JSON.parse(data.serviceAccountJSON)
                                } catch (err) {
                                    data.serviceAccountJSON = {}
                                }
                                data.applicationId = selectedApp.id;

                                let payload = { androidApp: data, appId:selectedApp.id, orgId: props.organisationId};
                                let result = await androidAppMutator.mutateAsync(payload)
                                console.log("androidConfig save result", result, payload, !!result);
                                return !!result;
                            } }
                            rules={{
                                serviceAccountJSON:[{ type: "string", required: true, message: "Service Account JSON should be a JSON serialisable string" , validator: (_, value:string) => {
                                    return new Promise<void>((resolve, reject)=>{
                                        let isValidJSON = false;
                                        try {
                                            JSON.parse(value);
                                            isValidJSON = true;
                                        } catch (jsonError:any) {
                                            reject(new Error("Service Account JSON should be a JSON serialisable string" ));
                                        } finally {
                                            resolve();    
                                        }
                                    })
                                }}],
                                firebaseProjectId:[{ type: "string", required: true, min: 5, message: "Firebase Project Id should be at least 5 characters long"}],
                            }}
                        />
                    </Flex>
                    {selectedApp.androidApplication && <ObjectToTable object={sortObjectKeys(selectedApp.androidApplication, ['applicationId', 'id', 'name', 'url'])}  filter={(key)=>!['applicationId', 'id', 'createdAt','updatedAt'].includes(key)}/>}

                    <Flex justify="space-between" align="baseline" gap={10}>
                        <Typography.Title level={5}>iOS Config</Typography.Title>
                        <ObjectToEditForm 
                            action={"Edit"}
                            title={"Edit iOS Config"}
                            object={{
                                p8FileBase64: selectedApp?.iOSApplication?.p8FileBase64 ?? "",
                                keyId: selectedApp?.iOSApplication?.keyId ?? "",
                                teamId: selectedApp?.iOSApplication?.teamId ?? "",
                                bundleId: selectedApp?.iOSApplication?.bundleId ?? "",
                                applicationId: selectedApp.id
                            }}
                            filter={(key) => !['applicationId','id', 'createdAt', 'updatedAt'].includes(key)} 
                            onSave={async function(data: any): Promise<boolean> {
                                
                                data.applicationId = selectedApp.id;
                                if(typeof data.p8FileBase64 == "object") {
                                    let dataUrlParts = data.p8FileBase64.file.response.split('base64,')
                                    data.p8FileBase64 = dataUrlParts[dataUrlParts.length - 1];
                                }

                                let payload = { iosApp: data, appId:selectedApp.id, orgId: props.organisationId};
                                let result = await iosAppMutator.mutateAsync(payload)
                                console.log("iosConfig save result", result, payload, !!result);
                                return !!result;
                            } }
                            rules={{
                                keyId: [{type: "string", validator:(_, value:string)=>{
                                    return new Promise<void>((resolve, reject)=>{
                                        let result = new RegExp(/^[A-Z0-9]+$/).test(value);
                                        if(result)
                                        {
                                            resolve();
                                        } else {
                                            reject(new Error("Key Id should be of the format of `[A-Z0-9]+`"));
                                        }
                                    })}
                                }],
                                teamId: [{ type: "string", validator: (_, value:string)=>{
                                    return new Promise<void>((resolve, reject)=>{
                                        let result = new RegExp(/^id[0-9]+$/).test(value);
                                        if(result)
                                        {
                                            resolve();
                                        } else {
                                            reject(new Error("Team Id should be of the format of `id[0-9]+`"));
                                        }
                                    })
                                }}],
                                bundleId: [{ type: "string", validator: (_, value:string)=>{
                                    return new Promise<void>((resolve, reject)=>{
                                        let result = new RegExp(/^([a-z0-9]+)(\.([a-z0-9]+)){2,}$/).test(value);
                                        if(result)
                                        {
                                            resolve();
                                        } else {
                                            reject(new Error("BundleId should be of the format of `^([a-z0-9]+)(\.([a-z0-9]+)){2,}`"));
                                        }
                                    })
                                    
                                }}]
                            }}
                            inputLookup={{p8FileBase64: <FileUploader />}}
                        />
                    </Flex>
                    {selectedApp.iOSApplication && <ObjectToTable object={sortObjectKeys(selectedApp.iOSApplication, ['applicationId', 'id', 'name', 'url'])}  
                                                                filter={(key)=>!['applicationId', 'id'].includes(key)}
                                                                transform={{ p8FileBase64: (value:any)=> {
                                                                    
                                                
                                                                    let fileStr = base64ToString(value);

                                                                    return (
                                                                        <Typography.Text copyable={{ text: fileStr, format: 'text/plain'}} style={{width: '100%', display: "inline-block"}}>
                                                                            <pre style={{ width: 'calc(100% - 40px)',display:"inline-flex"}}>
                                                                                {fileStr}
                                                                            </pre>
                                                                        </Typography.Text>
                                                                    )
                                                                }}}
                                                                labelTransform={{p8FileBase64: "p8 File"}}
                                                                />}
                </Flex>}
                
            </Flex>
        </Flex>
    )
}


const sortObjectKeys = <T extends Record<string, any>>(obj: T, customOrder: string[]): T => {
    const sortedEntries = Object.entries(obj)
        .sort(([keyA], [keyB]) => {
            const indexA = customOrder.indexOf(keyA);
            const indexB = customOrder.indexOf(keyB);
            
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB; // Sort by custom order if both keys are in the list
            } else if (indexA !== -1) {
                return -1; // Keys in custom order come first
            } else if (indexB !== -1) {
                return 1; // Keys in custom order come first
            }
            
            return keyA.localeCompare(keyB); // Sort alphabetically for remaining keys
        });
    
    return Object.fromEntries(sortedEntries) as T;
}



